/**
 * MOB casualty leeway — the RNLI ALB SAR Check Card 10 leeway table.
 *
 * When someone or something is adrift, the tide carries it, but the wind adds a
 * push of its own: leeway. How much depends on what's in the water. A person
 * floating low makes very little; a sports boat with a lot of hull up in the
 * wind makes a great deal. The RNLI card gives a leeway rate (nautical miles per
 * hour) for a range of casualty types across wind speeds from 5 to 100 knots, so
 * a search can estimate how far downwind the casualty has gone on top of the
 * tidal set.
 *
 * We keep the card's numbers verbatim (per casualty type, one figure per 5-knot
 * wind column) and interpolate between the columns, because the real wind is
 * rarely exactly on a column. Leeway acts WITH the wind, i.e. downwind.
 */

/** The wind-speed columns the RNLI card is tabulated against, knots. */
export const LEEWAY_WIND_COLS_KN: readonly number[] = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
];

export interface CasualtyType {
  /** Stable id used by the store and the UI. */
  id: string;
  /** Human label as it reads on the card. */
  label: string;
  /**
   * Leeway rate in NM per hour, one per column in LEEWAY_WIND_COLS_KN. Verbatim
   * from RNLI ALB SAR Check Card 10.
   */
  rates: readonly number[];
}

/**
 * The casualty types from the card, in the card's order. A person in the water
 * makes the least leeway; the sports boats and an unballasted liferaft the most.
 */
export const CASUALTY_TYPES: readonly CasualtyType[] = [
  {
    id: 'piw_unknown',
    label: 'Person in water — unknown state',
    rates: [0.11, 0.18, 0.24, 0.29, 0.35, 0.4, 0.46, 0.51, 0.57, 0.62, 0.68, 0.73, 0.79, 0.84, 0.9, 0.95, 1.01, 1.06, 1.12, 1.17],
  },
  {
    id: 'piw_pfd',
    label: 'Person in water — PFD, type unknown',
    rates: [0.12, 0.2, 0.26, 0.32, 0.39, 0.45, 0.51, 0.58, 0.64, 0.71, 0.77, 0.83, 0.9, 0.96, 1.02, 1.09, 1.15, 1.21, 1.28, 1.34],
  },
  {
    id: 'pwc',
    label: 'Personal watercraft (jet ski)',
    rates: [0.21, 0.42, 0.63, 0.84, 1.05, 1.26, 1.47, 1.68, 1.89, 2.1, 2.31, 2.52, 2.73, 2.94, 3.15, 3.36, 3.57, 3.78, 3.99, 4.2],
  },
  {
    id: 'windsurfer',
    label: 'Windsurfer (mast & sail in water)',
    rates: [0.2, 0.33, 0.45, 0.56, 0.68, 0.79, 0.91, 1.02, 1.14, 1.25, 1.37, 1.48, 1.6, 1.71, 1.83, 1.94, 2.06, 2.17, 2.29, 2.4],
  },
  {
    id: 'kayak_sit_on',
    label: 'Sit-on kayak, 1 POB',
    rates: [0.21, 0.42, 0.64, 0.85, 1.06, 1.27, 1.48, 1.7, 1.91, 2.12, 2.33, 2.54, 2.76, 2.97, 3.18, 3.39, 3.6, 3.82, 4.03, 4.24],
  },
  {
    id: 'sailing_dinghy',
    label: 'Small sailing dinghy',
    rates: [0.12, 0.23, 0.35, 0.46, 0.58, 0.69, 0.81, 0.92, 1.04, 1.15, 1.27, 1.38, 1.5, 1.61, 1.73, 1.84, 1.96, 2.07, 2.19, 2.3],
  },
  {
    id: 'sup',
    label: 'Stand-up paddleboard, 1 POB',
    rates: [0.14, 0.28, 0.41, 0.55, 0.69, 0.83, 0.96, 1.1, 1.24, 1.38, 1.51, 1.65, 1.79, 1.93, 2.06, 2.2, 2.34, 2.48, 2.61, 2.75],
  },
  {
    id: 'surfboard',
    label: 'Surfboard with person',
    rates: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0],
  },
  {
    id: 'fishing_vessel',
    label: 'Commercial fishing vessel (avg)',
    rates: [0.2, 0.39, 0.58, 0.76, 0.95, 1.13, 1.32, 1.5, 1.69, 1.87, 2.06, 2.24, 2.43, 2.61, 2.8, 2.98, 3.17, 3.35, 3.54, 3.72],
  },
  {
    id: 'sportsboat_console',
    label: 'Sports boat — centre console',
    rates: [0.23, 0.51, 0.81, 1.11, 1.41, 1.71, 2.01, 2.31, 2.61, 2.91, 3.21, 3.51, 3.81, 4.11, 4.41, 4.71, 5.01, 5.31, 5.61, 5.91],
  },
  {
    id: 'sportsboat_cuddy',
    label: 'Sports boat — cuddy cabin',
    rates: [0.28, 0.61, 0.96, 1.3, 1.65, 1.99, 2.34, 2.68, 3.03, 3.37, 3.72, 4.06, 4.41, 4.75, 5.1, 5.44, 5.79, 6.13, 6.48, 6.82],
  },
  {
    id: 'liferaft_ballast',
    label: 'Liferaft — deep ballast & canopy (avg)',
    rates: [0.17, 0.32, 0.47, 0.62, 0.77, 0.92, 1.07, 1.22, 1.37, 1.52, 1.67, 1.82, 1.97, 2.12, 2.27, 2.42, 2.57, 2.72, 2.87, 3.02],
  },
  {
    id: 'liferaft_none',
    label: 'Liferaft — no ballast / canopy / drogue',
    rates: [0.46, 0.78, 1.07, 1.35, 1.64, 1.92, 2.21, 2.49, 2.78, 3.06, 3.35, 3.63, 3.92, 4.2, 4.49, 4.77, 5.06, 5.34, 5.63, 5.91],
  },
];

/** The default casualty type: a person in the water, state unknown. */
export const DEFAULT_CASUALTY_ID = 'piw_unknown';

/** Look a casualty type up by id, falling back to the default (never null). */
export function casualtyTypeById(id: string): CasualtyType {
  return CASUALTY_TYPES.find((c) => c.id === id) ?? CASUALTY_TYPES[0];
}

/**
 * Leeway rate for a casualty type at a given wind speed, NM per hour.
 *
 * Between the card's 5-knot columns we interpolate linearly, because the wind is
 * rarely sitting exactly on a column. Below the first column (5 kt) we run the
 * line down to the origin (no wind, no leeway), which keeps it continuous and
 * physically honest: leeway is wind-driven, so at a flat calm it's zero. Above
 * the last column (100 kt) we hold the top figure rather than extrapolate into
 * wind speeds the card never measured.
 */
export function leewayRateNmPerHr(type: CasualtyType, windKn: number): number {
  const w = Number.isFinite(windKn) ? Math.max(0, windKn) : 0;
  const cols = LEEWAY_WIND_COLS_KN;
  const rates = type.rates;
  const first = cols[0];
  const last = cols[cols.length - 1];
  // Below the first column: line from (0 kt, 0 NM) up to (5 kt, rates[0]).
  if (w <= first) return (rates[0] * w) / first;
  // At or above the last column: hold the top figure.
  if (w >= last) return rates[rates.length - 1];
  // Between two columns: linear interpolation.
  for (let i = 0; i + 1 < cols.length; i++) {
    const lo = cols[i];
    const hi = cols[i + 1];
    if (w >= lo && w <= hi) {
      const f = (w - lo) / (hi - lo);
      return rates[i] + (rates[i + 1] - rates[i]) * f;
    }
  }
  return rates[rates.length - 1];
}
